import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import apiService from '../services/api';
import TempStart from './TempStart';
import AppShell from '../components/AppShell';
import SquadCover from '../components/squads/SquadCover';
import SquadCodeModal from '../components/squads/SquadCodeModal';
import ConfirmDialog from '../components/squads/ConfirmDialog';
import { apiErrorMessage } from '../utils/gameSchedule';
import { COLLAPSED_SQUAD_ROWS, memberRoleLabel } from '../utils/squadRows';
import '../styles/Games.css';
import '../styles/Squad.css';

const COVER_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];

function BackIcon() {
  return (
    <svg viewBox="0 0 16 16" aria-hidden="true">
      <path d="M10 3.5 5.5 8 10 12.5" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

// The embed's Squad Settings: the cover, the name, Add members, the roster with its role
// labels and Remove, and Delete Squad for the admin or Leave Squad for everyone else.
function SquadSettingsPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [squad, setSquad] = useState(null);
  const [failed, setFailed] = useState(false);
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [codeOpen, setCodeOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [coverBusy, setCoverBusy] = useState(false);
  const errorRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    apiService
      .getSquad(id)
      .then((data) => {
        if (cancelled) return;
        setSquad(data);
        setName(data?.name || '');
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  useEffect(() => {
    if (error && errorRef.current) errorRef.current.scrollIntoView({ block: 'nearest' });
  }, [error]);

  const isAdmin = !!(squad && (squad.is_admin || squad.isAdmin));

  const saveName = async () => {
    if (!isAdmin || !squad) return;
    const next = name.trim();
    if (!next) {
      setName(squad.name || '');
      return;
    }
    if (next === squad.name) return;
    try {
      const updated = await apiService.updateSquad(id, { name: next });
      setSquad(updated);
      setName(updated?.name || next);
      setError('');
    } catch (err) {
      setName(squad.name || '');
      setError(apiErrorMessage(err, 'Could not save the name.'));
    }
  };

  const replaceCover = async (event) => {
    const file = event.target.files && event.target.files[0];
    if (!file || !isAdmin) return;
    if (!COVER_TYPES.includes(file.type)) {
      setError('Unsupported file type. Please upload a JPG, PNG, GIF or WEBP image.');
      return;
    }
    setCoverBusy(true);
    try {
      const updated = await apiService.updateSquad(id, { coverFile: file });
      setSquad(updated);
      setError('');
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not update the cover.'));
    } finally {
      setCoverBusy(false);
    }
  };

  const removeMember = (member) => setConfirm({
    message: `Remove ${member.display_name || 'Fan'} from ${squad.name}?`,
    label: 'Remove',
    run: async () => {
      const updated = await apiService.removeSquadMember(id, member.id);
      setSquad(updated);
    },
    failure: 'Could not remove that fan.',
  });

  const dangerAction = () => setConfirm(isAdmin ? {
    message: `Delete ${squad.name} for everyone?`,
    label: 'Delete',
    run: async () => {
      await apiService.deleteSquad(id);
      navigate('/profile');
    },
    failure: 'Could not delete the squad.',
  } : {
    message: `Leave ${squad.name}?`,
    label: 'Leave',
    run: async () => {
      await apiService.leaveSquad(id);
      navigate('/profile');
    },
    failure: 'Could not leave.',
  });

  const runConfirm = async () => {
    if (!confirm || busy) return;
    setBusy(true);
    try {
      await confirm.run();
      setConfirm(null);
      setError('');
    } catch (err) {
      setConfirm(null);
      setError(apiErrorMessage(err, confirm.failure));
    } finally {
      setBusy(false);
    }
  };

  const members = Array.isArray(squad?.members) ? squad.members : [];
  const shown = expanded ? members : members.slice(0, COLLAPSED_SQUAD_ROWS);

  return (
    <div className="squad-page squad-settings">
      <header className="squad-bar">
        <Link to={`/squads/${encodeURIComponent(id)}`} className="squad-back is-dark" aria-label="Back" onClick={saveName}><BackIcon /></Link>
        <h1>Squad Settings</h1>
      </header>
      {failed ? <p className="squad-error">Could not load settings.</p> : null}
      {!failed && !squad ? <p className="games-empty">Loading settings...</p> : null}
      {squad ? (
        <>
          <SquadCover squad={squad} className="squad-settings-cover">
            {isAdmin ? (
              <label className={`squad-cover-replace${coverBusy ? ' is-busy' : ''}`}>
                {coverBusy ? 'Saving…' : squad.cover_url ? 'Replace photo' : 'Add a cover'}
                <input type="file" accept={COVER_TYPES.join(',')} onChange={replaceCover} disabled={coverBusy} />
              </label>
            ) : null}
          </SquadCover>

          <section className="squad-section">
            <h2>Edit name</h2>
            <input
              className="squad-name-input"
              type="text"
              value={name}
              maxLength={80}
              readOnly={!isAdmin}
              onChange={(event) => setName(event.target.value)}
              onBlur={saveName}
              onKeyDown={(event) => {
                if (event.key === 'Enter') event.target.blur();
              }}
              aria-label="Squad name"
            />
            {error ? <p ref={errorRef} className="squad-error">{error}</p> : null}
          </section>

          <section className="squad-section">
            <h2>Actions</h2>
            <button type="button" className="games-btn is-ghost squad-action" onClick={() => setCodeOpen(true)}>Add members</button>
          </section>

          <section className="squad-section">
            <h2>Members</h2>
            <ul className="squad-rows">
              {shown.map((member) => (
                <li key={String(member.id)} className={`squad-row${member.is_you ? ' is-you' : ''}`}>
                  <span className="squad-row-name">
                    {member.is_you ? 'You' : member.display_name || 'Fan'}
                    <em>{memberRoleLabel(member)}</em>
                  </span>
                  {!member.is_you ? (
                    <span className="squad-row-actions">
                      {member.fan_id ? <Link to={`/hc_prof/${encodeURIComponent(member.fan_id)}`}>View profile</Link> : null}
                      {isAdmin ? <button type="button" onClick={() => removeMember(member)}>Remove from squad</button> : null}
                    </span>
                  ) : null}
                </li>
              ))}
            </ul>
            {members.length > COLLAPSED_SQUAD_ROWS ? (
              <button type="button" className="squad-more" onClick={() => setExpanded((open) => !open)}>
                {expanded ? 'Show less' : 'Show more'}
              </button>
            ) : null}
          </section>

          <section className="squad-section">
            <button type="button" className="games-btn is-danger squad-action" onClick={dangerAction}>
              {isAdmin ? 'Delete Squad' : 'Leave Squad'}
            </button>
          </section>
        </>
      ) : null}
      {codeOpen ? <SquadCodeModal code={squad?.join_code || ''} onClose={() => setCodeOpen(false)} /> : null}
      {confirm ? <ConfirmDialog message={confirm.message} confirmLabel={confirm.label} busy={busy} onConfirm={runConfirm} onClose={() => (busy ? null : setConfirm(null))} /> : null}
    </div>
  );
}

function SquadSettingsGate() {
  if (!apiService.isAuthenticated()) return <TempStart />;
  return (
    <AppShell title="Squad">
      <SquadSettingsPage />
    </AppShell>
  );
}

export default SquadSettingsGate;
